/**
 * 添付ダウンロードリンクの記録一覧 `/dlp/mail/downloads`(設計 §10.1〜10.2・§10.4、段 6)。
 * approver は全記録、viewer は完了した取り出しの集計(uid × 宛先ドメイン × 送信者)、送信者本人は「自分の記録」で自分の分だけ全項目。
 * 条件(uid・宛先ドメイン・期間)は URL に載せる。メールの行が保持期限で消えた後も記録は残る。
 */
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Input, Space, Tag } from "antd";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import { DataTable, FilterBar, PageHeader, PopClose, TimeAgo, dateRangeCondition, useSearchParamInput, useSearchParamsPatch, type ConditionDef, type DataColumn } from "../../ui";
import { bytesText } from "./bits";
import { useDlpPerms } from "./hooks";
import { MailHelp } from "./MailHelp";
import { downloadsErrorText, downloadsParams, isDownloadSummary } from "./mailFiles";
import { useMailScope } from "./mailScope";
import type { MailDownloadRecord, MailDownloadSummary, MailPerson } from "./mailTypes";
import { mailError } from "./mailUtils";

function uidCell(uid: string, sender: MailPerson | null) {
  return <div><Link to={`/dlp/mail/messages/${uid}`} className="ui-num">{uid}</Link><span className="ui-cell-sub">{sender?.name ?? "送信者 —"}</span></div>;
}
function timeCell(iso: string | null) {
  return iso ? <div><TimeAgo iso={iso} /><span className="ui-cell-sub ui-num">{jst(iso)}</span></div> : "";
}

const RECORD_COLUMNS: DataColumn<MailDownloadRecord>[] = [
  { title: "日時", ellipsis: false, dataIndex: "at", width: 170, render: (v: string) => timeCell(v) },
  { title: "取引・送信者", ellipsis: false, width: 360, render: (_: unknown, r) => uidCell(r.uid, r.sender) },
  { title: "ファイル", ellipsis: false, width: 240, render: (_: unknown, r) => <div><span>{r.file_name ?? "(名前なし)"}</span><span className="ui-cell-sub ui-num">{bytesText(r.file_size)}</span></div> },
  { title: "宛先", ellipsis: false, width: 240, render: (_: unknown, r) => <div><span className="ui-num">{r.recipient_address}</span><span className="ui-cell-sub">{r.recipient_domain}</span></div> },
  { title: "接続元", ellipsis: true, onCell: (row) => ({ title: [row.ip, row.user_agent].filter(Boolean).join("\n") }), width: 360, render: (_: unknown, r) => <div><span className="ui-num">{r.ip ?? "—"}</span><span className="ui-cell-sub">{r.user_agent ?? ""}</span></div> },
  { title: "結果", ellipsis: false, width: 120, render: (_: unknown, r) => <div>{r.completed ? <Tag color="success">完了</Tag> : <Tag color="warning">中断</Tag>}<span className="ui-cell-sub ui-num">{r.bytes_sent == null ? "" : bytesText(r.bytes_sent)}</span></div> },
];
const SUMMARY_COLUMNS: DataColumn<MailDownloadSummary>[] = [
  { title: "取引・送信者", ellipsis: false, width: 360, render: (_: unknown, r) => uidCell(r.uid, r.sender) },
  { title: "宛先ドメイン", ellipsis: false, dataIndex: "recipient_domain", width: 240 },
  { title: "完了した取り出し", ellipsis: false, dataIndex: "count", width: 144, render: (v: number) => <span className="ui-num">{v} 件</span> },
  { title: "最終ダウンロード", ellipsis: false, dataIndex: "last_download_at", width: 170, render: (v: string | null) => timeCell(v) },
];

export function MailDownloadsPage() {
  const api = useApi();
  const scope = useMailScope();
  const { canApprove } = useDlpPerms();
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [uidInput, setUidInput] = useSearchParamInput("uid");
  const uid = sp.get("uid") ?? "";
  const domain = sp.get("recipient_domain") ?? "";
  const since = sp.get("since") ?? "";
  const until = sp.get("until") ?? "";
  // approver はサーバーが scope を見ずに全記録を返す。それ以外は「集計(all)」と「自分の記録(mine)」を明示して送り、
  // サーバーの既定(自分の記録があれば本人分、なければ集計)に表示を揺らされない
  const view: "all" | "mine" = !canApprove && sp.get("scope") === "mine" ? "mine" : "all";
  const [domainInput, setDomainInput] = useState(domain);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const filterKey = `${uid}|${domain}|${since}|${until}|${view}`;
  useEffect(() => setPage(1), [filterKey]);
  const params = downloadsParams({ uid, recipientDomain: domain, since, until, scope: canApprove ? "" : view, limit: pageSize, offset: (page - 1) * pageSize });
  const query = useQuery({ queryKey: ["dlp", "mail", scope, "file-downloads", params.toString()], gcTime: 0, retry: false,
    queryFn: ({ signal }) => api.mailFileDownloadsPage(params, signal) });
  const items = query.isError ? [] : query.data?.items ?? [];
  const total = query.isError ? 0 : query.data?.total ?? 0;
  const summary = !canApprove && view === "all";
  const records = items.filter((r): r is MailDownloadRecord => !isDownloadSummary(r));
  const summaries = items.filter(isDownloadSummary);

  const conditions: ConditionDef[] = [
    {
      key: "recipient_domain", label: "宛先ドメイン", value: domain,
      onClear: () => { setDomainInput(""); patch({ recipient_domain: null }, true); },
      control: (close) => <>
        <Input autoFocus style={{ width: 240 }} value={domainInput} placeholder="outside.example" onChange={(e) => setDomainInput(e.target.value)}
          onPressEnter={() => { patch({ recipient_domain: domainInput.trim().toLowerCase() || null }, true); close(); }} />
        <PopClose onClick={close} />
      </>,
    },
    dateRangeCondition({ key: "range", label: "期間(JST)", from: since, to: until, onChange: (from, to) => patch({ since: from || null, until: to || null }, true) }),
  ];
  const filtered = !!(uid || domain || since || until);
  const clearAll = () => { setUidInput(""); setDomainInput(""); patch({ uid: null, recipient_domain: null, since: null, until: null }, true); };
  const errorText = query.isError ? downloadsErrorText(query.error) ?? mailError(query.error) : null;
  const tableError = query.isError ? "取得できなかったので表示できない" : undefined;
  const emptyText = view === "mine" ? "自分の送信に対する取り出しの記録はありません" : summary ? "完了した取り出しの記録はありません" : "取り出しの記録はありません";
  const onPage = (p: number, size: number) => { setPage(p); setPageSize(size); };

  return <div className="ui-list"><div className="ui-list-main">
    <PageHeader title="ダウンロード記録"
      description={canApprove ? "宛先がリンクからファイルを取り出した記録です。宛先アドレス・ファイル名・接続元を含み、メールの行が保持期限で消えた後も残ります(取引のリンク先は無いことがあります)。"
        : summary ? "完了した取り出しの件数と最終日時を、取引・宛先ドメイン・送信者ごとに集計しています。宛先アドレス・ファイル名・接続元は dlp:approver 以上にだけ開示します。"
        : "自分が送ったメールのリンクから、宛先がファイルを取り出した記録です。宛先アドレス・ファイル名・接続元を含みます。"} />
    <MailHelp />
    <FilterBar
      search={{ value: uidInput, onChange: setUidInput, placeholder: "取引 uid" }}
      views={canApprove ? undefined : { items: [{ key: "all", label: "集計" }, { key: "mine", label: "自分の記録" }], active: view, onChange: (key) => patch({ scope: key === "mine" ? "mine" : null }, true) }}
      conditions={conditions}
      right={<Space size={8}>
        <Button size="small" onClick={() => void query.refetch()}>再取得</Button>
        {filtered && <Button size="small" onClick={clearAll}>条件を外す</Button>}
      </Space>}
    />
    {errorText && <Alert type="error" showIcon message={errorText} style={{ marginBottom: 12 }} />}
    {summary
      ? <DataTable<MailDownloadSummary> columns={SUMMARY_COLUMNS} rows={summaries} rowKey={(r) => `${r.uid}|${r.recipient_domain}|${r.sender?.id ?? ""}`} loading={query.isFetching}
          total={total} page={page} pageSize={pageSize} onPage={onPage} unit="件" empty={{ text: emptyText }} error={tableError} />
      : <DataTable<MailDownloadRecord> columns={RECORD_COLUMNS} rows={records} rowKey={(r) => r.id} loading={query.isFetching}
          total={total} page={page} pageSize={pageSize} onPage={onPage} unit="件" empty={{ text: emptyText }} error={tableError} />}
  </div></div>;
}
